/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import { createAsyncThunk, createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { RootState } from "..";
import { siteService } from "../../services/site-service";
import { ISite } from "../../types/site";
import { SiteTree } from "../../views/radios/Site";
import { IPSEC_MODE } from "../../constants";
import { ObjectReference } from "../../types/dataObject";
import {
  ChannelAllocationStrategy,
  ChannelBlockKind,
  ChannelPciRange,
  ChannelPoolBand,
  CHANNEL_ALLOCATION_STRATEGY_STATIC_PAIRS,
} from "../../types/channelPool";
import {
  defaultSiteChannelPool,
  newEmptyPool,
} from "../../helpers/channelPoolDefaults";

type initState = {
  open: boolean;
  siteEditFromExcelImport: boolean;
  isEdit: boolean;
  current: ISite | null;
  editExcelData?: ISite | null;
  data: ISite[];
  siteNZone: SiteTree[];
  error: string | null | undefined;
  fetchDone: boolean;
  expanded: string[];
  dashboardSelectedSite: ISite | null;
  loading: boolean;
  editState: ISite | null;
  enbIdError: string | null;
  carrierKPIExport: {
    open: boolean;
    operationID: string | null;
    isRunning: boolean;
  };
  exportCiqOpen: boolean;
  importSiteOpen: boolean;
};

const initialState: initState = {
  open: false,
  siteEditFromExcelImport: false,
  isEdit: false,
  current: null,
  editExcelData: null,
  data: [],
  siteNZone: [],
  error: null,
  fetchDone: false,
  expanded: [],
  dashboardSelectedSite: null,
  loading: true,
  editState: null,

  enbIdError: null,
  carrierKPIExport: {
    open: false,
    operationID: null,
    isRunning: false,
  },
  exportCiqOpen: false,
  importSiteOpen: false,
};

export const fetchSites = createAsyncThunk("site/fetchSites", async (query?: { timestamp?: number }) => {
  const timestamp = query?.timestamp;
  const queryString = timestamp ? { timestamp } : {};
  const _sites = (await siteService.getSites(queryString)).sort((a, b) => a.name.localeCompare(b.name));
  return _sites;
});

const siteSlice = createSlice({
  name: "site",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setSiteEditFromExcelImport: (state, action: PayloadAction<boolean>) => {
      state.siteEditFromExcelImport = action.payload;
    },
    setIsEdit: (state, action: PayloadAction<boolean>) => {
      state.isEdit = action.payload;
    },
    setValues: (state, action: PayloadAction<ISite | null>) => {
      state.current = action.payload;
    },
    setData: (state, action: PayloadAction<ISite[]>) => {
      state.data = action.payload;
    },

    setSiteNZone: (state, action: PayloadAction<any>) => {
      state.siteNZone = action.payload;
    },
    setFetchDone: (state, action: PayloadAction<boolean>) => {
      state.fetchDone = action.payload;
    },
    setExpanded: (state, action: PayloadAction<string[]>) => {
      state.expanded = action.payload;
    },
    setDashboardSelectedSite: (state, action: PayloadAction<ISite | null>) => {
      state.dashboardSelectedSite = action.payload; 1
    },
    setEditState: (state, action: PayloadAction<ISite | null>) => {
      state.editState = action.payload;
    },

    setEnbIdError: (state, action: PayloadAction<string | null>) => {
      state.enbIdError = action.payload;
    },
    setCarrierKPIExportOpen: (state, action: PayloadAction<boolean>) => {
      state.carrierKPIExport.open = action.payload;
    },
    setCarrierKPIExportID: (state, action: PayloadAction<string | null>) => {
      state.carrierKPIExport.operationID = action.payload;
    },
    setCarrierKPIExportRunning: (state, action: PayloadAction<boolean>) => {
      state.carrierKPIExport.isRunning = action.payload;
    },
    exportCiqOpen: (state, action: PayloadAction<boolean>) => {
      state.exportCiqOpen = action.payload;
    },
    // EDIT SITE STATE ACTIONS

    // General
    setName: (state, action: PayloadAction<string>) => {
      if (!state.editState) return;
      state.editState.name = action.payload;
    },
    setDescription: (state, action: PayloadAction<string>) => {
      if (!state.editState) return;
      state.editState.description = action.payload;
    },
    setAttributes: (state, action: PayloadAction<string[]>) => {
      if (!state.editState) return;
      state.editState.attributes = action.payload;
    },
    setTacId: (state, action: PayloadAction<string>) => {
      if (!state.editState) return;
      state.editState.tacId = action.payload;
    },
    setSiteImage: (state, action: PayloadAction<{ imageData: string; imageName: string; imageId?: string }[]>) => {
      if (!state.editState) return;
      state.editState.siteImage = action.payload
    },
    setSiteInfo: (state, action: PayloadAction<ISite['siteInfo']>) => {
      if (!state.editState) return;
      state.editState.siteInfo = action.payload;
    },
    setSitePOCInfo: (state, action: PayloadAction<ISite['sitePOCInfo']>) => {
      if (!state.editState) return;
      state.editState.sitePOCInfo = action.payload;
    },

    setLocation: (state, action: PayloadAction<ISite['location']>) => {
      if (!state.editState) return;
      state.editState.location = action.payload;
    },

    // Carrier Extensions
    setSiteService: (state, action: PayloadAction<ISite['siteService']>) => {
      if (!state.editState) return;
      state.editState.siteService = action.payload;
    },

    setCarrierCellIDBlocks: (state, action: PayloadAction<number[]>) => {
      if (!state.editState) return;
      state.editState.macroEnbIdList = action.payload;
    },

    // Networking
    setEdge: (state, action: PayloadAction<{ value: ObjectReference; index: number }>) => {
      if (!state.editState) return;
      if (!state.editState.edges) {
        state.editState.edges = [];
      }
      if (state.editState.edges[action.payload.index]) {
        state.editState.edges[action.payload.index].edge = action.payload.value;
      }
    },
    addNewEdge: (state) => {
      if (!state.editState) return;
      if (!state.editState.edges) {
        state.editState.edges = [];
      }
      state.editState.edges.push({ edge: { id: "" }, ipsecMode: IPSEC_MODE.PSK });
    },
    removeEdge: (state, action: PayloadAction<number>) => {
      if (!state.editState || !state.editState.edges) return;
      state.editState.edges = state.editState.edges.filter((_, i) => i !== action.payload);
    },
    setIPsecMode: (state, action: PayloadAction<{ value: string; index: number }>) => {
      if (!state.editState || !state.editState.edges || !state.editState.edges[action.payload.index]) return;
      state.editState.edges[action.payload.index].ipsecMode = action.payload.value;
    },
    setCarrier: (state, action: PayloadAction<{ value: ObjectReference[]; index: number }>) => {
      if (!state.editState || !state.editState.edges || !state.editState.edges[action.payload.index]) return;
      state.editState.edges[action.payload.index].carriers = action.payload.value;
    },
    setIsCloudGatewayEnabled: (state, action: PayloadAction<boolean>) => {
      if (!state.editState) return;
      state.editState.isCloudGatewayEnabled = action.payload;
    },
    setCloudGatewayEdge: (state, action: PayloadAction<ObjectReference>) => {
      if (!state.editState) return;
      state.editState.cloudGatewayEdge = action.payload;
    },
    setImportSiteOpen: (state, action: PayloadAction<boolean>) => {
      state.importSiteOpen = action.payload;
    },

    // ── Cell & Frequency (channelPool) ──────────────────────────────
    initChannelPoolWithDefaults: (state) => {
      if (!state.editState) return;
      if (!state.editState.channelPool) {
        state.editState.channelPool = defaultSiteChannelPool;
      }
    },
    setSitePciRange: (
      state,
      action: PayloadAction<{
        kind: "borderRadioPciRange" | "nonBorderRadioPciRange";
        field: keyof ChannelPciRange;
        value: string;
      }>
    ) => {
      if (!state.editState) return;
      if (!state.editState.channelPool)
        state.editState.channelPool = defaultSiteChannelPool;
      const { kind, field, value } = action.payload;
      state.editState.channelPool[kind][field] = value === "" ? "" : Number(value);
    },
    addChannelPool: (state) => {
      if (!state.editState) return;
      if (!state.editState.channelPool)
        state.editState.channelPool = defaultSiteChannelPool;
      state.editState.channelPool.channelPool.push(newEmptyPool());
    },
    deleteChannelPool: (state, action: PayloadAction<number>) => {
      if (!state.editState?.channelPool) return;
      state.editState.channelPool.channelPool.splice(action.payload, 1);
    },
    setPoolBand: (
      state,
      action: PayloadAction<{
        poolIndex: number;
        block: ChannelBlockKind;
        value: ChannelPoolBand;
      }>
    ) => {
      if (!state.editState?.channelPool) return;
      const { poolIndex, block, value } = action.payload;
      const pool = state.editState.channelPool.channelPool[poolIndex];
      if (!pool) return;
      pool[block].band = value;
    },
    setPoolBandwidth: (
      state,
      action: PayloadAction<{
        poolIndex: number;
        block: ChannelBlockKind;
        value: number;
        /**
         * Channel groups to seed for the new bandwidth (channels are
         * bandwidth-specific, so the previous groups are always discarded).
         * Falls back to a single empty pair when not provided.
         */
        channelGroups?: number[][];
      }>
    ) => {
      if (!state.editState?.channelPool) return;
      const { poolIndex, block, value, channelGroups } = action.payload;
      const pool = state.editState.channelPool.channelPool[poolIndex];
      if (!pool) return;
      pool[block].bandwidth = value;
      pool[block].channelGroups = channelGroups ?? [[""]];
    },
    /** Replaces a block's entire pair list (used by the pairing Reset). */
    setChannelGroupsForBlock: (
      state,
      action: PayloadAction<{
        poolIndex: number;
        block: ChannelBlockKind;
        channelGroups: number[][];
      }>
    ) => {
      if (!state.editState?.channelPool) return;
      const { poolIndex, block, channelGroups } = action.payload;
      const pool = state.editState.channelPool.channelPool[poolIndex];
      if (!pool) return;
      pool[block].channelGroups = channelGroups;
    },
    addChannelGroup: (
      state,
      action: PayloadAction<{ poolIndex: number; block: ChannelBlockKind }>
    ) => {
      if (!state.editState?.channelPool) return;
      const { poolIndex, block } = action.payload;
      const pool = state.editState.channelPool.channelPool[poolIndex];
      if (!pool) return;
      pool[block].channelGroups.push(["", ""]);
    },
    deleteChannelGroup: (
      state,
      action: PayloadAction<{
        poolIndex: number;
        block: ChannelBlockKind;
        groupIndex: number;
      }>
    ) => {
      if (!state.editState?.channelPool) return;
      const { poolIndex, block, groupIndex } = action.payload;
      const pool = state.editState.channelPool.channelPool[poolIndex];
      if (!pool) return;
      pool[block].channelGroups.splice(groupIndex, 1);
    },
    setChannelInGroup: (
      state,
      action: PayloadAction<{
        poolIndex: number;
        block: ChannelBlockKind;
        groupIndex: number;
        channelIndex: number;
        value: number | string;
      }>
    ) => {
      if (!state.editState?.channelPool) return;
      const { poolIndex, block, groupIndex, channelIndex, value } =
        action.payload;
      const pool = state.editState.channelPool.channelPool[poolIndex];
      if (!pool) return;
      const group = pool[block].channelGroups[groupIndex];
      if (!group) return;
      // Pad with empty strings if the user clicks the secondary slot first.
      while (group.length <= channelIndex) group.push("");
      group[channelIndex] = value;
    },

    setChannelAllocationStrategy: (
      state,
      action: PayloadAction<ChannelAllocationStrategy>
    ) => {
      if (!state.editState) return;
      state.editState.channelAllocationStrategy = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSites.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(fetchSites.rejected, (state, action) => {
        console.log(action);
        state.error = action.error.message;
        state.loading = false;
      });
  },
});

export const siteActions = siteSlice.actions;
export default siteSlice.reducer;
export const siteState = (state: RootState) => state.site.current;
export const siteData = (state: RootState) => state.site.data;
export const siteLoading = (state: RootState) => state.site.loading;
export const siteCount = (state: RootState) => state.site.data.length;
export const siteNZone = (state: RootState) => state.site.siteNZone;
export const siteOpen = (state: RootState) => state.site.open;
export const siteEditFromExcelImport = (state: RootState) => state.site.siteEditFromExcelImport;
export const siteEdit = (state: RootState) => state.site.isEdit;
export const fetchDone = (state: RootState) => state.site.fetchDone;
export const siteExpanded = (state: RootState) => state.site.expanded;
export const siteCurrentDashboard = (state: RootState) => state.site.dashboardSelectedSite;
export const siteEditState = (state: RootState) => state.site.editState;
export const siteEnbIdError = (state: RootState) => state.site.enbIdError;
export const siteByID = (id: string) => (state: RootState) => state.site.data.find((site) => site.id === id);
export const siteCarrierKPIExportOpen = (state: RootState) => state.site.carrierKPIExport.open;
export const siteCarrierKPIExportID = (state: RootState) => state.site.carrierKPIExport.operationID;
export const siteCarrierKPIExportRunning = (state: RootState) => state.site.carrierKPIExport.isRunning;
export const siteExportCiqOpen = (state: RootState) => state.site.exportCiqOpen;
export const siteImportOpen = (state: RootState) => state.site.importSiteOpen;

// Derived Selector for Site and Enb List for Site
export const siteEnbLists = (state: RootState) => state.site.data.map((site, i) => ({
  id: site.id,
  name: site.name,
  macroEnbIdList: site.macroEnbIdList
})).filter((site) => site.macroEnbIdList?.length);


/**
 * Network technology for the site currently being edited in the dialog.
 *
 * Derived from the FIRST associated edge — this intentionally mirrors the
 * backend (`deriveRadioTechnologyForSite` uses `site.Edges[0]`), so UI tab
 * gating and backend channel-pool validation agree.
 *
 * Used to gate visibility of the Frequency tab (Phase 1 = 4G only).
 */
export const siteEditEdgeTechnology = createSelector(
  (state: RootState) => state.site.editState,
  (state: RootState) => state.edge.data,
  (state: RootState) => state.edge.edgesWithPool,
  (editState, edgeList, edgesWithPools): "4G" | "5G" | undefined => {
    const firstEdgeId = editState?.edges?.[0]?.edge?.id;
    if (!firstEdgeId) return undefined;

    // Edge pool — resolve technology via any member edge in the pool.
    if (firstEdgeId.startsWith("edgepool-")) {
      const tech = edgesWithPools.find((p) => p.edgePoolId === firstEdgeId)?.networkType;
      return tech === "4G" || tech === "5G" ? tech : undefined;
    }

    // Direct edge.
    const tech = edgeList.find((edge) => edge.id === firstEdgeId)?.networkType;
    return tech === "4G" || tech === "5G" ? (tech as "4G" | "5G") : undefined;
  }
);

/**
 * Default Channel Allocation Strategy for new 4G sites — sourced from the
 * `Default.Channel.Allocation.Strategy` system setting, falling back to
 * STATIC_PAIRS when the setting is missing. The setting value already uses
 * the same upper-case enum as the per-site field, so no mapping is needed.
 */
export const defaultChannelAllocationStrategy = (state: RootState): ChannelAllocationStrategy => {
  const raw = state.settings.system.find(
    (s) => s.name === "Default.Channel.Allocation.Strategy"
  )?.value;
  if (
    raw === "CA_CHANNEL_ALLOCATION_STRATEGY_STATIC_PAIRS" ||
    raw === "CA_CHANNEL_ALLOCATION_STRATEGY_SHARED_SECONDARY"
  ) {
    return raw;
  }
  return CHANNEL_ALLOCATION_STRATEGY_STATIC_PAIRS;
};

export const siteHas5GEdge = createSelector(
  (state: RootState) => state.site.current,
  (state: RootState) => state.edge.data,
  (state: RootState) => state.edge.edgesWithPool,
  (currentSite, edgeList, edgesWithPools) => {
    const edges = currentSite?.edges || [];
     // Find if there's a selected edge pool
    // MCS-3778: edge.edge.id can be undefined; guard the .startsWith call.
    const selectedEdgePoolId = edges.find(edge => edge?.edge?.id?.startsWith("edgepool-"))?.edge?.id;
    const selectedEdgePoolTechnology = selectedEdgePoolId 
    ? edgesWithPools.find(edge => edge.edgePoolId === selectedEdgePoolId)?.networkType 
    : undefined;

  // Check if any edge has 5G network type or if the selected edge pool technology is 5G
  return edges.some((e) => {
    const _edge = edgeList.find((edge) => edge.id === e.edge.id);
      return _edge?.networkType === "5G" || selectedEdgePoolTechnology === "5G";
    });
  }
);
